import { createContext, useContext, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { motion } from 'framer-motion'
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ContactSupportRoundedIcon from '@mui/icons-material/ContactSupportRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import HubRoundedIcon from '@mui/icons-material/HubRounded'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded'
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded'
import RouteRoundedIcon from '@mui/icons-material/RouteRounded'
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded'
import SpaceDashboardRoundedIcon from '@mui/icons-material/SpaceDashboardRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded'
import TokenRoundedIcon from '@mui/icons-material/TokenRounded'
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded'
import WorkspacesRoundedIcon from '@mui/icons-material/WorkspacesRounded'
import timeliningLogo from '../assets/logo.png'
import { useAuth } from '../context/useAuth'
import englishPublicSiteContent from '../content/publicSiteContent.json'
import {
  getPublicSiteContent,
  getPublicSiteRouteMeta,
} from '../nls/public/publicSiteContent'
import {
  PUBLIC_SITE_LOCALE_LABELS,
  PUBLIC_SITE_MAJOR_LOCALES,
  getStoredPublicSiteLocale,
  setStoredPublicSiteLocale,
} from '../nls/public/localePolicy'
import type {
  PublicSiteLocale,
  PublicSiteNls,
} from '../nls/public/publicSiteTypes'
import { DownloadPage } from './DownloadPage'
import { LegalDocument } from '../components/Common/LegalDocument'
import { useNls } from '../nls/nlsProvider'
import privacyMarkdown from '../content/legal/privacy.md?raw'
import termsMarkdown from '../content/legal/terms.md?raw'
import refundsMarkdown from '../content/legal/refunds.md?raw'
import grievanceMarkdown from '../content/legal/grievance.md?raw'
import './publicSite.css'

export type PublicRouteKey =
  | 'overview'
  | 'solutions'
  | 'docs'
  | 'resources'
  | 'pricing'
  | 'about'
  | 'faq'
  | 'contact'
  | 'download'
  | 'privacy'
  | 'terms'
  | 'refunds'
  | 'grievance'

type PublicSiteProps = {
  path: string
}

export const ROUTE_BY_PATH: Record<string, PublicRouteKey> = {
  '/': 'overview',
  '/solutions': 'solutions',
  '/docs': 'docs',
  '/resources': 'resources',
  '/pricing': 'pricing',
  '/about': 'about',
  '/faq': 'faq',
  '/contact': 'contact',
  '/download': 'download',
  '/privacy': 'privacy',
  '/terms': 'terms',
  '/refunds': 'refunds',
  '/grievance': 'grievance',
}

export const PUBLIC_SITE_PATHS = new Set(Object.keys(ROUTE_BY_PATH))

export const getPublicRouteKey = (path: string): PublicRouteKey | null => ROUTE_BY_PATH[path] ?? null

export const getPublicRouteMeta = (path: string) =>
  englishPublicSiteContent.routes.find((route) => route.path === path) ?? englishPublicSiteContent.routes[0]

const PublicNlsContext = createContext<PublicSiteNls>(getPublicSiteContent('en'))

const usePublicNls = () => useContext(PublicNlsContext)

const iconMap = {
  timeline: TimelineRoundedIcon,
  spark: AutoAwesomeRoundedIcon,
  groups: GroupsRoundedIcon,
  shield: ShieldRoundedIcon,
  hub: HubRoundedIcon,
  stars: StarRoundedIcon,
  wires: HubRoundedIcon,
  chat: ContactSupportRoundedIcon,
  focus: BoltRoundedIcon,
  credits: TokenRoundedIcon,
  raid: LocalFireDepartmentRoundedIcon,
  reports: DescriptionRoundedIcon,
  trophy: StarRoundedIcon,
  token: TokenRoundedIcon,
  curse: LocalFireDepartmentRoundedIcon,
  sparkle: AutoAwesomeRoundedIcon,
  grid: SpaceDashboardRoundedIcon,
}

const openConsole = () => {
  window.open('/console', '_blank', 'noopener,noreferrer')
}

const pageTransition = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { type: 'spring', stiffness: 120, damping: 24 },
} as const

const PublicNav = ({
  currentPath,
  signedIn,
  locale,
  onLocaleChange,
}: {
  currentPath: string
  signedIn: boolean
  locale: PublicSiteLocale
  onLocaleChange: (locale: PublicSiteLocale) => void
}) => {
  const { content, chrome } = usePublicNls()
  const visibleRoutes = content.routes.filter((route) =>
    ['/', '/solutions', '/docs', '/resources', '/pricing', '/about', '/faq'].includes(route.path),
  )

  return (
    <header className="publicNav">
      <a className="publicBrand" href="/" aria-label={`${content.brand.name} ${chrome.nav.brandHomeSuffix}`}>
        <img src={timeliningLogo} alt="" aria-hidden="true" />
        <span>{content.brand.name}</span>
      </a>
      <nav className="publicNavLinks" aria-label={chrome.nav.ariaLabel}>
        {visibleRoutes.map((route) => (
          <a key={route.path} href={route.path} aria-current={currentPath === route.path ? 'page' : undefined}>
            {route.label}
          </a>
        ))}
      </nav>
      <div className="publicNavActions">
        <label className="publicLanguageSelect">
          <TranslateRoundedIcon fontSize="small" />
          <span>{chrome.nav.languageLabel}</span>
          <select
            value={locale}
            onChange={(event) => onLocaleChange(event.target.value as PublicSiteLocale)}
            aria-label={chrome.nav.languageAriaLabel}
          >
            {PUBLIC_SITE_MAJOR_LOCALES.map((option) => (
              <option key={option} value={option}>
                {PUBLIC_SITE_LOCALE_LABELS[option].nativeLabel}
              </option>
            ))}
          </select>
        </label>
        <a className="publicNavGhost" href="/contact">{chrome.nav.contact}</a>
        <button type="button" className="publicNavConsole" onClick={openConsole}>
          {signedIn ? chrome.nav.console : chrome.nav.getStarted}
          <ArrowOutwardRoundedIcon fontSize="small" />
        </button>
      </div>
    </header>
  )
}

const Hero = ({ signedIn }: { signedIn: boolean }) => {
  const { content, chrome } = usePublicNls()
  return (
    <section className="publicHero" aria-labelledby="public-hero-title">
      <div className="publicHeroBackdrop" aria-hidden="true">
        <span className="publicOrbit publicOrbitOne" />
        <span className="publicOrbit publicOrbitTwo" />
        <span className="publicOrbit publicOrbitThree" />
      </div>
      <motion.div className="publicHeroCopy" {...pageTransition}>
        <p className="publicEyebrow">{content.hero.eyebrow}</p>
        <h1 id="public-hero-title">
          {content.hero.headline.split(content.hero.accent)[0]}
          <span>{content.hero.accent}</span>
          {content.hero.headline.split(content.hero.accent)[1]}
        </h1>
        <p className="publicHeroBody">{content.hero.body}</p>
        <div className="publicHeroActions">
          <button type="button" className="publicMegaButton" onClick={openConsole}>
            <RocketLaunchRoundedIcon />
            {signedIn ? content.hero.primaryCtaSignedIn : content.hero.primaryCtaSignedOut}
            <ArrowOutwardRoundedIcon />
          </button>
          <a className="publicQuietButton" href="/docs">
            <MenuBookRoundedIcon />
            {content.hero.secondaryCta}
          </a>
        </div>
        <p className="publicConsoleNote">
          <LockRoundedIcon fontSize="small" />
          {signedIn ? content.hero.consoleNoteSignedIn : content.hero.consoleNoteSignedOut}
        </p>
      </motion.div>
      <motion.div
        className="publicHeroPanel"
        initial={{ opacity: 0, rotate: -2, y: 28 }}
        animate={{ opacity: 1, rotate: 0, y: 0 }}
        transition={{ delay: 0.08, type: 'spring', stiffness: 96, damping: 18 }}
        aria-label="Timelining public preview"
      >
        <div className="publicPanelTop">
          <span />
          <span />
          <span />
        </div>
        <div className="publicPanelMap">
          <div className="publicMapNode publicMapNodeHot">{chrome.overview.visualGoal}</div>
          <div className="publicMapLine publicMapLineOne" />
          <div className="publicMapNode publicMapNodeBlue">{chrome.overview.visualAi}</div>
          <div className="publicMapLine publicMapLineTwo" />
          <div className="publicMapNode publicMapNodeGreen">{chrome.overview.visualTeam}</div>
          <div className="publicMapNode publicMapNodeDark">{chrome.overview.visualReview}</div>
        </div>
        <div className="publicPanelTicker">
          {content.hero.badges.map((badge) => <span key={badge}>{badge}</span>)}
        </div>
      </motion.div>
    </section>
  )
}

const Marquee = () => {
  const { content } = usePublicNls()
  return (
    <div className="publicMarquee" aria-label="Timelining capabilities">
      <div>
        {[...content.marquee, ...content.marquee].map((item, index) => (
          <span key={`${item}-${index}`}>{item}</span>
        ))}
      </div>
    </div>
  )
}

const SignalPanels = () => {
  const { content } = usePublicNls()
  return (
    <section className="publicSignalGrid" aria-label="Timelining product signals">
      {content.signalPanels.map((panel) => (
        <article key={panel.kicker} className="publicSignalPanel">
          <span>{panel.kicker}</span>
          <h2>{panel.title}</h2>
          <p>{panel.body}</p>
        </article>
      ))}
    </section>
  )
}

const FeatureGrid = () => {
  const { content, chrome } = usePublicNls()
  return (
    <section className="publicSection" id="features">
      <div className="publicSectionHeading">
        <p className="publicEyebrow">{chrome.overview.featureEyebrow}</p>
        <h2>{chrome.overview.featureTitle}</h2>
      </div>
      <div className="publicFeatureGrid">
        {content.featureGrid.map((feature) => {
          const Icon = iconMap[feature.icon as keyof typeof iconMap] ?? StarRoundedIcon
          return (
            <article key={feature.title} className="publicFeature">
              <Icon />
              <h3>{feature.title}</h3>
              <p>{feature.body}</p>
            </article>
          )
        })}
      </div>
    </section>
  )
}

const StartBand = ({ signedIn }: { signedIn: boolean }) => {
  const { content } = usePublicNls()
  return (
    <section className="publicStartBand" aria-labelledby="public-start-title">
      <div>
        <p className="publicEyebrow">{content.start.eyebrow}</p>
        <h2 id="public-start-title">{content.start.title}</h2>
        <p>{content.start.body}</p>
      </div>
      <ul>
        {content.start.steps.map((point) => (
          <li key={point}>
            <KeyboardArrowRightRoundedIcon />
            <span>{point}</span>
          </li>
        ))}
      </ul>
      <button type="button" className="publicMegaButton publicMegaButtonDark publicStartCta" onClick={openConsole}>
        <SpaceDashboardRoundedIcon />
        {signedIn ? content.start.ctaSignedIn : content.start.ctaSignedOut}
        <ArrowOutwardRoundedIcon />
      </button>
    </section>
  )
}

const DataModelSection = () => {
  const { content, chrome } = usePublicNls()
  return (
    <section className="publicDataSection" aria-labelledby="public-data-title">
      <div className="publicSectionHeading">
        <p className="publicEyebrow">{chrome.overview.dataEyebrow}</p>
        <h2 id="public-data-title">{chrome.overview.dataTitle}</h2>
      </div>
      <div className="publicDataLadder">
        {content.dataModel.map((item, index) => (
          <article key={item.label} style={{ '--step': index } as CSSProperties}>
            <span>{String(index + 1).padStart(2, '0')}</span>
            <h3>{item.label}</h3>
            <p>{item.body}</p>
            <strong>{item.example}</strong>
          </article>
        ))}
      </div>
    </section>
  )
}

const VisualGuide = ({ visual }: { visual: string }) => {
  const { chrome } = usePublicNls()
  return (
    <div className={`publicGuideVisual publicGuideVisual--${visual}`} aria-hidden="true">
      {visual === 'rail' ? (
        <>
          <span />
          <span />
          <span />
          <span />
          <i />
        </>
      ) : visual === 'tree' ? (
        <>
          <strong>{chrome.overview.visualGoal}</strong>
          <span>{chrome.overview.visualTask}</span>
          <span>{chrome.overview.visualSubtask}</span>
          <span>{chrome.overview.visualChecklist}</span>
        </>
      ) : visual === 'wires' ? (
        <>
          <span />
          <span />
          <span />
          <i />
          <b />
        </>
      ) : (
        <>
          <strong>{chrome.overview.visualAi}</strong>
          <span>{chrome.overview.visualDraft}</span>
          <span>{chrome.overview.visualReview}</span>
          <span>{chrome.overview.visualSave}</span>
        </>
      )}
    </div>
  )
}

const ClickGuideSection = () => {
  const { content, chrome } = usePublicNls()
  return (
    <section className="publicClickGuide" aria-labelledby="public-click-title">
      <div className="publicSectionHeading">
        <p className="publicEyebrow">{chrome.overview.clickEyebrow}</p>
        <h2 id="public-click-title">{chrome.overview.clickTitle}</h2>
      </div>
      <div className="publicClickGrid">
        {content.clickGuides.map((guide) => (
          <article key={guide.title}>
            <VisualGuide visual={guide.visual} />
            <div>
              <AutoAwesomeRoundedIcon />
              <h3>{guide.title}</h3>
              <p>{guide.body}</p>
              <strong>{guide.click}</strong>
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}

const WorkflowSection = () => {
  const { content, chrome } = usePublicNls()
  return (
    <section className="publicWorkflowSection" aria-labelledby="public-workflows-title">
      <div className="publicSectionHeading">
        <p className="publicEyebrow">{chrome.overview.workflowEyebrow}</p>
        <h2 id="public-workflows-title">{chrome.overview.workflowTitle}</h2>
      </div>
      <div className="publicWorkflowGrid">
        {content.workflows.map((workflow) => (
          <article key={workflow.name}>
            <h3>{workflow.name}</h3>
            <p>{workflow.outcome}</p>
            <ol>
              {workflow.steps.map((step) => <li key={step}>{step}</li>)}
            </ol>
          </article>
        ))}
      </div>
    </section>
  )
}

const MediaGuideSection = () => {
  const { content, chrome } = usePublicNls()
  return (
    <section className="publicMediaGuide" aria-labelledby="public-media-title">
      <div className="publicMediaIntro">
        <p className="publicEyebrow">{chrome.overview.mediaEyebrow}</p>
        <h2 id="public-media-title">{chrome.overview.mediaTitle}</h2>
        <p>{chrome.overview.mediaIntro}</p>
      </div>
      <div className="publicMediaGrid">
        {content.mediaExamples.map((item) => (
          <article key={item.title}>
            <div className="publicMediaFrame">
              <video autoPlay muted loop playsInline preload="metadata" aria-label={`${item.title} demo`}>
                <source src={item.src} type="video/mp4" />
              </video>
              <span>{item.type}</span>
            </div>
            <h3>{item.title}</h3>
            <p>{item.body}</p>
            <strong>{item.status}</strong>
            <a className="publicMediaFallback" href={item.fallback}>{chrome.overview.mediaFallback}</a>
          </article>
        ))}
      </div>
    </section>
  )
}

const ScreenExamplesSection = () => {
  const { content, chrome } = usePublicNls()
  return (
    <section className="publicScreenshots" aria-labelledby="public-screens-title">
      <div className="publicSectionHeading">
        <p className="publicEyebrow">{chrome.overview.screenshotEyebrow}</p>
        <h2 id="public-screens-title">{chrome.overview.screenshotTitle}</h2>
      </div>
      <div className="publicScreenshotGrid">
        {content.screenExamples.map((item) => (
          <article key={item.title}>
            <img src={item.src} alt={item.title} loading="lazy" />
            <div>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}

const LiveStatsSection = () => {
  const { content } = usePublicNls()
  return (
    <section className="publicLiveStats" aria-labelledby="live-stats-title">
      <div className="publicSectionHeading">
        <p className="publicEyebrow">{content.liveStats.eyebrow}</p>
        <h2 id="live-stats-title">{content.liveStats.heading}</h2>
        <p>{content.liveStats.note}</p>
      </div>
      <div className="publicLiveStatsGrid">
        {content.liveStats.stats.map((stat) => (
          <article key={stat.label}>
            <strong>{stat.value}</strong>
            <span>{stat.label}</span>
            <p>{stat.note}</p>
          </article>
        ))}
      </div>
    </section>
  )
}

const ThreeShellsSection = () => {
  const { content } = usePublicNls()
  return (
    <section className="publicThreeShells" aria-labelledby="three-shells-title">
      <div className="publicSectionHeading">
        <p className="publicEyebrow">{content.threeShells.eyebrow}</p>
        <h2 id="three-shells-title">{content.threeShells.heading}</h2>
        <p>{content.threeShells.note}</p>
      </div>
      <div className="publicThreeShellsGrid">
        {content.threeShells.shells.map((shell) => (
          <article key={shell.name}>
            <header>
              <h3>{shell.name}</h3>
              <span>{shell.audience}</span>
            </header>
            <p>{shell.body}</p>
            <ul>
              {shell.surfaces.map((surface) => <li key={surface}>{surface}</li>)}
            </ul>
          </article>
        ))}
      </div>
    </section>
  )
}

const RpgLoopSection = () => {
  const { content } = usePublicNls()
  return (
    <section className="publicRpgLoop" aria-labelledby="rpg-loop-title">
      <div className="publicSectionHeading">
        <p className="publicEyebrow">{content.rpgLoop.eyebrow}</p>
        <h2 id="rpg-loop-title">{content.rpgLoop.heading}</h2>
        <p>{content.rpgLoop.note}</p>
      </div>
      <div className="publicRpgLoopGrid">
        {content.rpgLoop.loops.map((loop) => {
          const Icon = iconMap[loop.icon as keyof typeof iconMap] ?? StarRoundedIcon
          return (
            <article key={loop.name}>
              <Icon />
              <h3>{loop.name}</h3>
              <p>{loop.body}</p>
            </article>
          )
        })}
      </div>
    </section>
  )
}

const AiCeremonySection = () => {
  const { content } = usePublicNls()
  return (
    <section className="publicAiCeremony" aria-labelledby="ai-ceremony-title">
      <div className="publicSectionHeading">
        <p className="publicEyebrow">{content.aiCeremony.eyebrow}</p>
        <h2 id="ai-ceremony-title">{content.aiCeremony.heading}</h2>
        <p>{content.aiCeremony.note}</p>
      </div>
      <div className="publicAiCeremonySteps">
        {content.aiCeremony.steps.map((step) => (
          <article key={step.step}>
            <span className="publicAiCeremonyStep">{step.step}</span>
            <h3>{step.name}</h3>
            <p>{step.body}</p>
          </article>
        ))}
      </div>
    </section>
  )
}

const RolesTableSection = () => {
  const { content, chrome } = usePublicNls()
  return (
    <section className="publicRolesTable" aria-labelledby="roles-table-title">
      <div className="publicSectionHeading">
        <p className="publicEyebrow">{content.rolesTable.eyebrow}</p>
        <h2 id="roles-table-title">{content.rolesTable.heading}</h2>
        <p>{content.rolesTable.note}</p>
      </div>
      <div className="publicRolesTableWrap" role="table">
        <div className="publicRolesTableHeader" role="row">
          <div role="columnheader">{chrome.common.capabilityColumnHeader}</div>
          {content.rolesTable.columns.map((column) => (
            <div key={column} role="columnheader">{column}</div>
          ))}
        </div>
        {content.rolesTable.rows.map((row) => (
          <div key={row.capability} className="publicRolesTableRow" role="row">
            <div role="cell" className="publicRolesTableLabel">{row.capability}</div>
            {row.values.map((value, index) => (
              <div key={`${row.capability}-${index}`} role="cell">{value}</div>
            ))}
          </div>
        ))}
      </div>
    </section>
  )
}

const CtaBandSection = ({ signedIn }: { signedIn: boolean }) => {
  const { content, chrome } = usePublicNls()
  return (
    <section className="publicCtaBand" aria-labelledby="cta-band-title">
      <div>
        <p className="publicEyebrow">{content.ctaBand.eyebrow}</p>
        <h2 id="cta-band-title">{content.ctaBand.title}</h2>
        <p>{content.ctaBand.body}</p>
      </div>
      <div className="publicCtaBandActions">
        <button type="button" className="publicMegaButton" onClick={openConsole}>
          <RocketLaunchRoundedIcon />
          {signedIn ? chrome.cta.signedInPrimary : content.ctaBand.primary}
          <ArrowOutwardRoundedIcon />
        </button>
        <a className="publicQuietButton" href="/download">
          <MenuBookRoundedIcon />
          {content.ctaBand.secondary}
        </a>
      </div>
    </section>
  )
}

const StorySections = () => {
  const { content, chrome } = usePublicNls()
  return (
    <section className="publicStoryStack" aria-label={chrome.overview.storyAriaLabel}>
      {content.sections.map((section, index) => (
        <article key={section.id} className="publicStory">
          <span className="publicStoryIndex">{String(index + 1).padStart(2, '0')}</span>
          <div>
            <p className="publicEyebrow">{section.eyebrow}</p>
            <h2>{section.title}</h2>
            <p>{section.body}</p>
          </div>
        </article>
      ))}
    </section>
  )
}

const DocsPage = () => {
  const { content, chrome } = usePublicNls()
  return (
    <PublicArticle
      eyebrow={chrome.docs.eyebrow}
      title={content.docs.title}
      intro={content.docs.intro}
      icon={<DescriptionRoundedIcon />}
    >
      <div className="publicGuideList">
        {content.docs.steps.map((step, index) => (
          <article key={step.title}>
            <span>{String(index + 1).padStart(2, '0')}</span>
            <h2>{step.title}</h2>
            <p>{step.body}</p>
          </article>
        ))}
      </div>
      <div className="publicChapterGrid">
        {content.docs.chapters.map((chapter) => (
          <article key={chapter.title}>
            <h2>{chapter.title}</h2>
            <ul>
              {chapter.items.map((item) => (
                <li key={item}>
                  <KeyboardArrowRightRoundedIcon />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
      <ClickGuideSection />
      <RolesTableSection />
      <MediaGuideSection />
      <section className="publicShortcuts" aria-labelledby="public-shortcuts-title">
        <div className="publicSectionHeading">
          <p className="publicEyebrow">{chrome.docs.keyboardEyebrow}</p>
          <h2 id="public-shortcuts-title">{content.docs.shortcuts.title}</h2>
        </div>
        <div className="publicShortcutsGrid">
          {content.docs.shortcuts.items.map((item) => (
            <article key={item.keys}>
              <kbd>{item.keys}</kbd>
              <span>{item.action}</span>
            </article>
          ))}
        </div>
      </section>
      <div className="publicTips">
        <h2>{chrome.docs.tipsTitle}</h2>
        {content.docs.tips.map((tip) => <p key={tip}>{tip}</p>)}
      </div>
    </PublicArticle>
  )
}

const FaqPage = () => {
  const { content, chrome } = usePublicNls()
  return (
    <PublicArticle
      eyebrow={chrome.faq.eyebrow}
      title={chrome.faq.title}
      intro={chrome.faq.intro}
      icon={<ContactSupportRoundedIcon />}
    >
      <div className="publicFaqCategories">
        {content.faq.map((category, categoryIndex) => (
          <section key={category.category} className="publicFaqCategory" aria-label={category.category}>
            <div className="publicFaqCategoryHeader">
              <span>{String(categoryIndex + 1).padStart(2, '0')}</span>
              <h2>{category.category}</h2>
            </div>
            <div className="publicFaqList">
              {category.items.map((item, itemIndex) => (
                <details key={item.question} open={categoryIndex === 0 && itemIndex === 0}>
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </section>
        ))}
      </div>
    </PublicArticle>
  )
}

const AboutPage = () => {
  const { content, chrome } = usePublicNls()
  return (
    <PublicArticle eyebrow={chrome.about.eyebrow} title={content.about.title} intro={content.about.body} icon={<RouteRoundedIcon />}>
      <section aria-labelledby="about-principles-title" className="publicAboutBlock">
        <div className="publicSectionHeading">
          <p className="publicEyebrow">{chrome.about.principlesEyebrow}</p>
          <h2 id="about-principles-title">{chrome.about.principlesTitle}</h2>
        </div>
        <div className="publicPrinciples">
          {content.about.principles.map((principle) => (
            <article key={principle}>
              <StarRoundedIcon />
              <p>{principle}</p>
            </article>
          ))}
        </div>
      </section>
      <section aria-labelledby="about-for-title" className="publicAboutBlock">
        <div className="publicSectionHeading">
          <p className="publicEyebrow">{chrome.about.fitEyebrow}</p>
          <h2 id="about-for-title">{content.about.forWho.heading}</h2>
        </div>
        <div className="publicAboutForGrid">
          <article className="publicAboutFor">
            <h3>{chrome.about.goodFit}</h3>
            <ul>
              {content.about.forWho.for.map((item) => (
                <li key={item}><CheckRoundedIcon fontSize="small" /><span>{item}</span></li>
              ))}
            </ul>
          </article>
          <article className="publicAboutNotFor">
            <h3>{chrome.about.notRightTool}</h3>
            <ul>
              {content.about.forWho.notFor.map((item) => (
                <li key={item}><span aria-hidden="true">—</span><span>{item}</span></li>
              ))}
            </ul>
          </article>
        </div>
      </section>
      <section aria-labelledby="about-shipped-title" className="publicAboutBlock">
        <div className="publicSectionHeading">
          <p className="publicEyebrow">{chrome.about.statusEyebrow}</p>
          <h2 id="about-shipped-title">{chrome.about.statusTitle}</h2>
        </div>
        <div className="publicAboutStatusGrid">
          <article>
            <h3>{content.about.shipped.heading}</h3>
            <ul>
              {content.about.shipped.items.map((item) => (
                <li key={item}><CheckRoundedIcon fontSize="small" /><span>{item}</span></li>
              ))}
            </ul>
          </article>
          <article>
            <h3>{content.about.roadmap.heading}</h3>
            <ul>
              {content.about.roadmap.items.map((item) => (
                <li key={item}><KeyboardArrowRightRoundedIcon fontSize="small" /><span>{item}</span></li>
              ))}
            </ul>
          </article>
        </div>
      </section>
    </PublicArticle>
  )
}

const PricingEconomyStrip = () => {
  const { content } = usePublicNls()
  const economy = content.pricing.economy
  return (
    <section className="publicPricingEconomy" aria-labelledby="pricing-economy-title">
      <div className="publicPricingEconomyIntro">
        <p className="publicEyebrow">{economy.eyebrow}</p>
        <h2 id="pricing-economy-title">{economy.heading}</h2>
        <p>{economy.note}</p>
      </div>
      <div className="publicPricingEconomyGrid">
        {economy.items.map((item) => (
          <article key={item.label}>
            <div className="publicPricingEconomyHeader">
              <BoltRoundedIcon fontSize="small" />
              <span>{item.label}</span>
            </div>
            <strong>{item.cost}</strong>
            <p>{item.note}</p>
          </article>
        ))}
      </div>
    </section>
  )
}

const PricingTiers = () => {
  const { content, chrome } = usePublicNls()
  return (
    <section className="publicPricingTiers" aria-label={chrome.pricing.tierAriaLabel}>
      {content.pricing.tiers.map((tier) => (
        <article
          key={tier.name}
          className={`publicPricingTier${tier.featured ? ' publicPricingTierFeatured' : ''}`}
        >
          {tier.featured && tier.featuredLabel ? (
            <span className="publicPricingTierBadge">{tier.featuredLabel}</span>
          ) : null}
          <header>
            <h3>{tier.name}</h3>
            <p className="publicPricingTierTagline">{tier.tagline}</p>
            <div className="publicPricingTierPrice">
              <strong>{tier.price}</strong>
              <span>{tier.cadence}</span>
            </div>
          </header>
          <dl className="publicPricingTierAllowance">
            <div>
              <dt><TokenRoundedIcon fontSize="small" /> {chrome.common.creditsLabel}</dt>
              <dd>{tier.credits}</dd>
            </div>
            <div>
              <dt><HubRoundedIcon fontSize="small" /> {chrome.common.storageLabel}</dt>
              <dd>{tier.storage}</dd>
            </div>
          </dl>
          <ul className="publicPricingTierHighlights">
            {tier.highlights.map((item) => (
              <li key={item}>
                <CheckRoundedIcon fontSize="small" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          {tier.limits && tier.limits.length > 0 ? (
            <ul className="publicPricingTierLimits">
              {tier.limits.map((item) => (
                <li key={item}>
                  <span aria-hidden="true">—</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {tier.name === 'Enterprise' ? (
            <a
              className={`publicPricingTierCta${tier.featured ? ' publicPricingTierCtaFeatured' : ''}`}
              href="/contact?topic=enterprise"
            >
              {tier.cta}
              <ArrowOutwardRoundedIcon fontSize="small" />
            </a>
          ) : (
            <button
              type="button"
              className={`publicPricingTierCta${tier.featured ? ' publicPricingTierCtaFeatured' : ''}`}
              onClick={openConsole}
            >
              {tier.cta}
              <ArrowOutwardRoundedIcon fontSize="small" />
            </button>
          )}
        </article>
      ))}
    </section>
  )
}

const PricingAddons = () => {
  const { content } = usePublicNls()
  const addons = content.pricing.addons
  return (
    <section className="publicPricingAddons" aria-labelledby="pricing-addons-title">
      <div className="publicPricingAddonsIntro">
        <p className="publicEyebrow">{addons.eyebrow}</p>
        <h2 id="pricing-addons-title">{addons.heading}</h2>
        <p>{addons.note}</p>
      </div>
      <div className="publicPricingAddonsGrid">
        {[addons.credits, addons.storage].map((group) => (
          <article key={group.title}>
            <h3>{group.title}</h3>
            <ul>
              {group.packs.map((pack) => (
                <li key={`${group.title}-${pack.amount}`}>
                  <span className="publicPricingPackAmount">{pack.amount}</span>
                  <span className="publicPricingPackPrice">{pack.price}</span>
                  {pack.savings ? <span className="publicPricingPackSavings">{pack.savings}</span> : <span aria-hidden="true" />}
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </section>
  )
}

const PricingComparison = () => {
  const { content, chrome } = usePublicNls()
  const comparison = content.pricing.comparison
  return (
    <section className="publicPricingComparison" aria-labelledby="pricing-comparison-title">
      <div className="publicPricingComparisonIntro">
        <p className="publicEyebrow">{comparison.eyebrow}</p>
        <h2 id="pricing-comparison-title">{comparison.heading}</h2>
        <p>{comparison.note}</p>
      </div>
      <div className="publicPricingComparisonTable" role="table" aria-label={chrome.pricing.comparisonAriaLabel}>
        <div className="publicPricingComparisonHeader" role="row">
          <div role="columnheader">{chrome.common.featureColumnHeader}</div>
          {comparison.columns.map((column) => (
            <div key={column} role="columnheader">{column}</div>
          ))}
        </div>
        {comparison.groups.map((group) => (
          <div key={group.title} className="publicPricingComparisonGroup">
            <div className="publicPricingComparisonGroupTitle" role="row">
              <div role="cell">{group.title}</div>
            </div>
            {group.rows.map((row) => (
              <div key={`${group.title}-${row.label}`} className="publicPricingComparisonRow" role="row">
                <div role="cell" className="publicPricingComparisonRowLabel">{row.label}</div>
                {row.values.map((value, index) => (
                  <div key={`${row.label}-${index}`} role="cell" data-column={comparison.columns[index]}>
                    {value}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  )
}

const PricingFaq = () => {
  const { content, chrome } = usePublicNls()
  return (
    <section className="publicPricingFaq" aria-labelledby="pricing-faq-title">
      <div className="publicPricingFaqIntro">
        <p className="publicEyebrow">{chrome.pricing.faqEyebrow}</p>
        <h2 id="pricing-faq-title">{chrome.pricing.faqTitle}</h2>
      </div>
      <div className="publicFaqList">
        {content.pricing.faq.map((item, index) => (
          <details key={item.question} open={index === 0}>
            <summary>{item.question}</summary>
            <p>{item.answer}</p>
          </details>
        ))}
      </div>
    </section>
  )
}

const PricingClosing = ({ signedIn }: { signedIn: boolean }) => {
  const { content, chrome } = usePublicNls()
  const closing = content.pricing.closing
  return (
    <section className="publicPricingClosing" aria-labelledby="pricing-closing-title">
      <div>
        <p className="publicEyebrow">{closing.eyebrow}</p>
        <h2 id="pricing-closing-title">{closing.title}</h2>
        <p>{closing.body}</p>
      </div>
      <div className="publicPricingClosingActions">
        <button type="button" className="publicMegaButton" onClick={openConsole}>
          <RocketLaunchRoundedIcon />
          {signedIn ? chrome.common.openWorkspace : closing.primaryCta}
          <ArrowOutwardRoundedIcon />
        </button>
        <a className="publicQuietButton" href="/contact">
          <LocalFireDepartmentRoundedIcon />
          {closing.secondaryCta}
        </a>
      </div>
    </section>
  )
}

const PricingPersonalTiers = () => {
  const { content, chrome } = usePublicNls()
  const personal = content.pricing.personalTiers
  return (
    <section className="publicPricingPersonal" aria-labelledby="pricing-personal-title">
      <div className="publicPricingPersonalIntro">
        <p className="publicEyebrow">{personal.eyebrow}</p>
        <h2 id="pricing-personal-title">{personal.heading}</h2>
        <p>{personal.note}</p>
      </div>
      <div className="publicPricingPersonalGrid">
        {personal.items.map((tier) => (
          <article key={tier.name}>
            <header>
              <h3>{tier.name}</h3>
              <p className="publicPricingPersonalTagline">{tier.tagline}</p>
              <div className="publicPricingPersonalPrice">
                <strong>{tier.price}</strong>
                <span>{tier.cadence}</span>
              </div>
            </header>
            <dl>
              <div><dt>{chrome.common.creditsLabel}</dt><dd>{tier.credits}</dd></div>
              <div><dt>{chrome.common.storageLabel}</dt><dd>{tier.storage}</dd></div>
            </dl>
            <ul>
              {tier.highlights.map((item) => (
                <li key={item}><CheckRoundedIcon fontSize="small" /><span>{item}</span></li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </section>
  )
}

const PRICING_LOCALES = [
  { code: 'en', label: PUBLIC_SITE_LOCALE_LABELS.en.nativeLabel, currency: '$ USD' },
  { code: 'zh-Hans', label: PUBLIC_SITE_LOCALE_LABELS['zh-Hans'].nativeLabel, currency: '¥ CNY' },
  { code: 'es', label: PUBLIC_SITE_LOCALE_LABELS.es.nativeLabel, currency: '€ EUR' },
  { code: 'hi', label: PUBLIC_SITE_LOCALE_LABELS.hi.nativeLabel, currency: '₹ INR' },
  { code: 'fr', label: PUBLIC_SITE_LOCALE_LABELS.fr.nativeLabel, currency: '€ EUR' },
  { code: 'ar', label: PUBLIC_SITE_LOCALE_LABELS.ar.nativeLabel, currency: 'د.إ AED' },
  { code: 'pt', label: PUBLIC_SITE_LOCALE_LABELS.pt.nativeLabel, currency: 'R$ BRL' },
] as const

const PricingLocaleBar = () => {
  const { content, chrome } = usePublicNls()
  const [locale, setLocale] = useState<typeof PRICING_LOCALES[number]['code']>('en')
  const active = PRICING_LOCALES.find((entry) => entry.code === locale) ?? PRICING_LOCALES[0]
  return (
    <div className="publicPricingLocaleBar">
      <label className="publicPricingLocale">
        <span className="publicPricingLocaleLabel">{chrome.pricing.localeLabel}</span>
        <select
          value={locale}
          onChange={(event) => setLocale(event.target.value as typeof locale)}
          aria-label={chrome.pricing.localeAriaLabel}
        >
          {PRICING_LOCALES.map((entry) => (
            <option key={entry.code} value={entry.code}>
              {entry.label} · {entry.currency}
            </option>
          ))}
        </select>
      </label>
      <p className="publicPricingCurrencyNote">
        <LockRoundedIcon fontSize="small" />
        {active.code === 'en'
          ? content.pricing.currencyNote
          : chrome.pricing.currencyNoteFallback.replace('{currency}', active.currency)}
      </p>
    </div>
  )
}

const PricingAudience = () => {
  const { content } = usePublicNls()
  const audience = content.pricing.audience
  return (
    <div className="publicPricingAudience">
      <p className="publicEyebrow">{audience.eyebrow}</p>
      <h2>{audience.heading}</h2>
      <p>{audience.note}</p>
    </div>
  )
}

const PricingPage = ({ signedIn }: { signedIn: boolean }) => {
  const { content, chrome } = usePublicNls()
  return (
    <PublicArticle
      eyebrow={chrome.pricing.eyebrow}
      title={content.pricing.title}
      intro={content.pricing.body}
      icon={<ShieldRoundedIcon />}
    >
      <PricingLocaleBar />
      <PricingEconomyStrip />
      <PricingAudience />
      <PricingTiers />
      <PricingPersonalTiers />
      <PricingAddons />
      <PricingComparison />
      <PricingFaq />
      <PricingClosing signedIn={signedIn} />
    </PublicArticle>
  )
}

const ContactPage = () => {
  const { content, chrome } = usePublicNls()
  const { t } = useNls()
  const c = content.contact
  return (
    <PublicArticle eyebrow={chrome.contact.eyebrow} title={c.title} intro={c.body} icon={<HubRoundedIcon />}>
      <p className="publicContactPromise">{c.responsePromise}</p>
      <div className="publicContactLayout">
        <form
          className="publicContactForm"
          onSubmit={(event) => {
            event.preventDefault()
            const form = event.currentTarget as HTMLFormElement
            const data = new FormData(form)
            const name = String(data.get('name') ?? '')
            const email = String(data.get('email') ?? '')
            const topic = String(data.get('topic') ?? '')
            const message = String(data.get('message') ?? '')
            const subject = encodeURIComponent(`[${topic || chrome.contact.mailtoFallbackTopic}] ${name || chrome.contact.mailtoFallbackName}`)
            const body = encodeURIComponent(`From: ${name} <${email}>\nTopic: ${topic}\n\n${message}`)
            window.location.href = `mailto:${c.email}?subject=${subject}&body=${body}`
          }}
        >
          <label>
            <span>{chrome.contact.nameLabel}</span>
            <input required name="name" type="text" placeholder={chrome.contact.namePlaceholder} />
          </label>
          <label>
            <span>{chrome.contact.emailLabel}</span>
            <input required name="email" type="email" placeholder={chrome.contact.emailPlaceholder} />
          </label>
          <label>
            <span>{chrome.contact.topicLabel}</span>
            <select name="topic" defaultValue={c.topics[0]?.value}>
              {c.topics.map((topic) => (
                <option key={topic.value} value={topic.value}>{topic.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>{chrome.contact.messageLabel}</span>
            <textarea required name="message" rows={6} placeholder={chrome.contact.messagePlaceholder} />
          </label>
          <button type="submit" className="publicMegaButton">
            <RocketLaunchRoundedIcon />
            {chrome.contact.sendMessage}
            <ArrowOutwardRoundedIcon />
          </button>
          <p className="publicContactDirect">
            {chrome.contact.directPrefix} <a href={`mailto:${c.email}`}>{c.email}</a>.
          </p>
          <p className="publicContactGrievance">
            {t('NLSContactGrievancePrefix')} <a href="/grievance">{t('NLSContactGrievanceLink')}</a>.
          </p>
        </form>
        <aside className="publicContactAside">
          <article className="publicContactWalkthrough">
            <h3>{c.walkthrough.title}</h3>
            <p>{c.walkthrough.body}</p>
            <a className="publicQuietButton" href={`mailto:${c.email}?subject=${encodeURIComponent(chrome.contact.walkthroughSubject)}`}>
              <CalendarMonthRoundedIcon />
              {c.walkthrough.cta}
            </a>
          </article>
          <article className="publicContactChannels">
            <h3>{c.channels.title}</h3>
            <ul>
              {c.channels.items.map((item) => (
                <li key={item.label}>
                  {item.href === '/console' ? (
                    <button type="button" onClick={openConsole}>
                      <strong>{item.label}</strong>
                      <span>{item.note}</span>
                      <ArrowOutwardRoundedIcon fontSize="small" />
                    </button>
                  ) : (
                    <a href={item.href}>
                      <strong>{item.label}</strong>
                      <span>{item.note}</span>
                      <KeyboardArrowRightRoundedIcon fontSize="small" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </article>
          <article className="publicContactTopics">
            <h3>{chrome.contact.topicExpectationsTitle}</h3>
            <ul>
              {c.topics.map((topic) => (
                <li key={topic.value}>
                  <strong>{topic.label}</strong>
                  <span>{topic.note}</span>
                </li>
              ))}
            </ul>
          </article>
        </aside>
      </div>
    </PublicArticle>
  )
}

const SolutionsPage = () => (
  <SolutionsPageInner />
)

const SolutionsPageInner = () => {
  const { content, chrome } = usePublicNls()
  return (
    <PublicArticle
      eyebrow={chrome.solutions.eyebrow}
      title={chrome.solutions.title}
      intro={chrome.solutions.intro}
      icon={<WorkspacesRoundedIcon />}
    >
      <div className="publicSolutionCases">
        {content.solutions.map((solution, index) => (
          <article key={solution.label}>
            <header>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h2>{solution.label}</h2>
            </header>
            <p className="publicSolutionSummary">{solution.body}</p>
            <div className="publicSolutionFields">
              <div>
                <span className="publicEyebrow">{chrome.solutions.setup}</span>
                <p>{solution.setup}</p>
              </div>
              <div>
                <span className="publicEyebrow">{chrome.solutions.dailyLoop}</span>
                <p>{solution.loop}</p>
              </div>
              <div>
                <span className="publicEyebrow">{chrome.solutions.difference}</span>
                <p>{solution.different}</p>
              </div>
            </div>
          </article>
        ))}
      </div>
      <WorkflowSection />
    </PublicArticle>
  )
}

const ResourcesPage = () => {
  const { content, chrome } = usePublicNls()
  const categories = [
    chrome.resources.categories.delivery,
    chrome.resources.categories.creative,
    chrome.resources.categories.personal,
    chrome.resources.categories.ops,
  ] as const
  const categoryLookup = {
    [chrome.resources.categories.delivery]: 'Delivery',
    [chrome.resources.categories.creative]: 'Creative',
    [chrome.resources.categories.personal]: 'Personal',
    [chrome.resources.categories.ops]: 'Ops',
  } as const
  const recipesByCategory = categories.map((category) => ({
    category,
    items: content.resources.recipes.filter((recipe) => recipe.category === categoryLookup[category]),
  }))
  return (
    <PublicArticle
      eyebrow={chrome.resources.eyebrow}
      title={content.resources.title}
      intro={content.resources.intro}
      icon={<CalendarMonthRoundedIcon />}
    >
      {recipesByCategory.map(({ category, items }) => (
        <section key={category} className="publicRecipeBlock" aria-label={`${category} ${chrome.resources.recipeAriaSuffix}`}>
          <div className="publicSectionHeading">
            <p className="publicEyebrow">{category}</p>
            <h2>{category} {chrome.resources.recipeHeadingSuffix}</h2>
          </div>
          <div className="publicRecipeGrid">
            {items.map((recipe) => (
              <article key={recipe.title}>
                <HubRoundedIcon />
                <h3>{recipe.title}</h3>
                <ul>
                  {recipe.items.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </article>
            ))}
          </div>
        </section>
      ))}
      <section className="publicPromptLibrary" aria-labelledby="prompt-library-title">
        <div className="publicSectionHeading">
          <p className="publicEyebrow">{chrome.resources.promptEyebrow}</p>
          <h2 id="prompt-library-title">{content.resources.prompts.heading}</h2>
          <p>{content.resources.prompts.note}</p>
        </div>
        <div className="publicPromptGroups">
          {content.resources.prompts.categories.map((group) => (
            <article key={group.name}>
              <h3>{group.name}</h3>
              <ul>
                {group.prompts.map((prompt) => (
                  <li key={prompt}>
                    <span aria-hidden="true">›</span>
                    <span>{prompt}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>
    </PublicArticle>
  )
}

const PublicArticle = ({
  eyebrow,
  title,
  intro,
  icon,
  children,
}: {
  eyebrow: string
  title: string
  intro: string
  icon: ReactNode
  children: ReactNode
}) => (
  <motion.main className="publicArticle" {...pageTransition}>
    <div className="publicArticleHero">
      <div className="publicArticleIcon">{icon}</div>
      <p className="publicEyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      <p>{intro}</p>
    </div>
    {children}
  </motion.main>
)

const OverviewPage = ({ signedIn }: { signedIn: boolean }) => {
  const { content, chrome } = usePublicNls()
  return (
    <main>
      <Hero signedIn={signedIn} />
      <Marquee />
      <LiveStatsSection />
      <SignalPanels />
      <ThreeShellsSection />
      <RpgLoopSection />
      <AiCeremonySection />
      <FeatureGrid />
      <StartBand signedIn={signedIn} />
      <DataModelSection />
      <ClickGuideSection />
      <RolesTableSection />
      <WorkflowSection />
      <MediaGuideSection />
      <ScreenExamplesSection />
      <StorySections />
      <CtaBandSection signedIn={signedIn} />
      <section className="publicDocsFaqPreview" aria-label={chrome.overview.docsFaqPreviewAriaLabel}>
        <a href="/docs">
          <MenuBookRoundedIcon />
          <span>{chrome.docs.eyebrow}</span>
          <strong>{content.docs.title}</strong>
          <p>{content.docs.intro}</p>
        </a>
        <a href="/faq">
          <ContactSupportRoundedIcon />
          <span>{chrome.overview.faqPreviewLabel}</span>
          <strong>{chrome.overview.faqPreviewTitle}</strong>
          <p>{content.faq[0]?.items[0]?.answer}</p>
        </a>
      </section>
    </main>
  )
}

export const PublicSite = ({ path }: PublicSiteProps) => {
  const { currentUser, isSessionLocked } = useAuth()
  const { t } = useNls()
  const [locale, setLocale] = useState<PublicSiteLocale>(getStoredPublicSiteLocale)
  const routeKey = getPublicRouteKey(path) ?? 'overview'
  const signedIn = Boolean(currentUser && !isSessionLocked)
  const nls = useMemo(() => getPublicSiteContent(locale), [locale])
  const routeMeta = useMemo(() => getPublicSiteRouteMeta(nls.content, path), [nls.content, path])
  const setPublicLocale = (nextLocale: PublicSiteLocale) => {
    setLocale(nextLocale)
    setStoredPublicSiteLocale(nextLocale)
  }

  useEffect(() => {
    setStoredPublicSiteLocale(locale)
  }, [locale])

  return (
    <PublicNlsContext.Provider value={nls}>
      <div
        className="publicSite"
        lang={nls.locale}
        dir={nls.typography.dir}
        style={{
          '--nls-font-scale': nls.typography.fontScale,
          '--nls-compact-font-scale': nls.typography.compactFontScale,
          '--nls-line-height': nls.typography.lineHeight,
          '--nls-compact-line-height': nls.typography.compactLineHeight,
        } as CSSProperties}
      >
        <PublicNav currentPath={path} signedIn={signedIn} locale={locale} onLocaleChange={setPublicLocale} />
        {routeKey === 'overview' && <OverviewPage signedIn={signedIn} />}
        {routeKey === 'solutions' && <SolutionsPage />}
        {routeKey === 'docs' && <DocsPage />}
        {routeKey === 'resources' && <ResourcesPage />}
        {routeKey === 'pricing' && <PricingPage signedIn={signedIn} />}
        {routeKey === 'about' && <AboutPage />}
        {routeKey === 'faq' && <FaqPage />}
        {routeKey === 'contact' && <ContactPage />}
        {routeKey === 'download' && <DownloadPage chrome={nls.chrome.download} />}
        {routeKey === 'privacy' && <LegalDocument rawMarkdown={privacyMarkdown} title={t('NLSPublicRoutePrivacyTitle')} />}
        {routeKey === 'terms' && <LegalDocument rawMarkdown={termsMarkdown} title={t('NLSPublicRouteTermsTitle')} />}
        {routeKey === 'refunds' && <LegalDocument rawMarkdown={refundsMarkdown} title={t('NLSPublicRouteRefundsTitle')} />}
        {routeKey === 'grievance' && <LegalDocument rawMarkdown={grievanceMarkdown} title={t('NLSPublicRouteGrievanceTitle')} />}
        <footer className="publicFooter">
          <div>
            <img src={timeliningLogo} alt="" aria-hidden="true" />
            <span>{nls.content.brand.name}</span>
          </div>
          <p>{routeMeta.description}</p>
          <nav className="publicFooterLegal" aria-label={t('NLSPublicFooterLegalNav')}>
            <a href="/privacy">{t('NLSPublicLinkPrivacy')}</a>
            <a href="/terms">{t('NLSPublicLinkTerms')}</a>
            <a href="/refunds">{t('NLSPublicLinkRefunds')}</a>
            <a href="/grievance">{t('NLSPublicLinkGrievance')}</a>
          </nav>
          <button type="button" onClick={openConsole}>
            {nls.chrome.common.console}
            <ArrowOutwardRoundedIcon fontSize="small" />
          </button>
        </footer>
      </div>
    </PublicNlsContext.Provider>
  )
}
